import Vue from 'vue'
import Vuex from 'vuex'
import pkg from '../package.json'

const { title } = pkg.config

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    error: null,
    loading: true,
    env: 'development',
    browser: '',
    touch: false,
    mobile: false,
    phone: false,
    route: '',
    slug: '',
    title: title,
    posts: [],

    navOpen: false,
    mediaOpen: false,
    sidebarOpen: true,
    categoriesOpen: true,

    ytIsPlaying: false,
    ytIsAudioDescribed: false,

    bgVideoIsPlaying: true,
    bgVideoWasPlaying: true,

    isIframed: false
  },

  getters: {
    posts: state => state.posts,
    post: state => state.posts.find(({ slug }) => slug === state.slug),
    options: state => state.posts.find(({ type }) => type === 'page'),
    campus: state => state.posts.find(({ type }) => type === 'campus'),
    subjects: state => state.posts.filter(({ type }) => type === 'subject'),
    guidedtour: state => state.posts.find(({ type }) => type === 'tour'),
    categories: state => state.posts.filter(({ type }) => type === 'category'),

    getPostBySlug: state => slug =>
      state.posts.find(item => item.slug === slug),

    getCampus: (state, getters) => slug => {
      const category = state.posts.find(item => item.slug === slug)

      const categoryLocations = category.children
        .filter(({ type }) => type === 'location')
        .map(({ id }) => id)

      const campusLocations = getters.campus.children.filter(item =>
        categoryLocations.includes(item.id)
      )

      const landmarkLocations = getters.campus.children.filter(
        ({ location_type }) => location_type === 'landmark_location'
      )

      const campus = Object.assign({}, getters.campus, {
        children: [...campusLocations, ...landmarkLocations]
      })

      return campus
    }
  },

  mutations: {
    browser(state, browser) {
      state.browser = browser
    },
    environment(state, env) {
      state.env = env
    },
    isLoading(state, loading) {
      state.loading = loading
    },
    isTouch(state) {
      state.touch = true
    },
    isMobile(state, mobile) {
      state.mobile = mobile
    },
    isPhone(state, phone) {
      state.phone = phone
    },
    route(state, route) {
      const slug = route.path.split('/').pop() || '/'
      state.route = route
      state.slug = slug
      const isMedia = route.meta.media
      state.mediaOpen = isMedia || false
    },
    toggleNavState(state) {
      state.navOpen = !state.navOpen
    },
    toggleCategoriesState(state, isOpen) {
      state.categoriesOpen = isOpen
    },
    toggleSidebarState(state, isOpen) {
      state.sidebarOpen = isOpen
    },
    toggleAudioDescription(state) {
      state.ytIsAudioDescribed = !state.ytIsAudioDescribed
    },
    toggleYouTubeState(state, playing) {
      state.ytIsPlaying = playing
    },
    toggleBgVideoState(state, playing) {
      state.bgVideoIsPlaying = playing
    },
    toggleBgVideoPrevState(state, wasPlaying) {
      state.bgVideoWasPlaying = wasPlaying
    },
    isIframed(state, isIframed) {
      state.isIframed = isIframed
    },
    populate(state, { posts, error } = {}) {
      state.error = error

      const data = posts.map(item => {
        const acf = item.acf

        return {
          title: item.title.rendered,
          type: item.type === 'post' ? 'article' : item.type,
          slug: `${item.slug}`,
          id: item.id,
          ...acf
        }
      })

      const panoramas = data
        .filter(({ type }) => type === 'panorama' || type === 'campus')
        .map(item => {
          const children = item.children || []

          const hotSpots = children.map(({ entry }) =>
            data.find(o => o.id === entry.ID)
          )

          return {
            ...item,
            children: hotSpots.map((o, i) => ({
              ...o,
              ...item.children[i].position
            }))
          }
        })

      // Essentially 360 groups for guided tours and locations
      const toursOrLocations = data
        .filter(({ type }) => type === 'tour' || type === 'location')
        .map(item => {
          const children = item.children || []
          if (item.type === 'tour') {
            return {
              ...item,
              children: children
                .map(o => panoramas.find(f => f.id === o))
                .filter(o => o),
              palette: {
                primary: 'primary',
                secondary: 'secondary',
                tertiary: 'tertiary'
              }
            }
          }
          return {
            ...item,
            children: children
              .map(o => panoramas.find(f => f.id === o))
              .filter(o => o)
          }
        })

      // Categories and subjects are basically the same
      // They contain conntent and locations for each entry
      const categoriesAndSubjects = data
        .filter(({ type }) => type === 'category' || type === 'subject')
        .map(item => {
          const children = item.children || []

          return {
            ...item,
            children: children
              .map(o => data.find(f => f.id === o))
              .filter(o => o)
              .filter(
                ({ location_type }) =>
                  // Need to filter out the landmark locations as they break if added in children
                  // These should probably be in their own post type
                  location_type !== 'landmark_location' || !location_type
              )
              .map(x => {
                const innerChildren = x.children || []
                return {
                  ...x,
                  children: innerChildren
                    .map(o => data.find(f => f.id === o))
                    .filter(o => o)
                }
              })
          }
        })

      // Adding a subjects post as a category
      categoriesAndSubjects.push({
        title: 'Subjects',
        heading: 'Which subject are you interested in?',
        slug: 'subjects',
        children: categoriesAndSubjects.filter(
          ({ type }) => type === 'subject'
        ),
        type: 'category'
      })

      // Add the color palette for each category
      const categories = categoriesAndSubjects
        .filter(({ type }) => type === 'category')
        .map((item, i) => {
          const acc = i * 3

          return {
            ...item,
            palette: {
              primary: `accent-${acc}`,
              secondary: `accent-${acc + 1}`,
              tertiary: `accent-${acc + 2}`
            }
          }
        })

      const subjects = categoriesAndSubjects
        .filter(({ type }) => type === 'subject')
        .map(item => {
          return {
            ...item,
            palette: {
              primary: 'accent-9',
              secondary: 'accent-10',
              tertiary: 'accent-11'
            }
          }
        })

      // Really just one page is used for site options ctas etc
      const pages = data
        .filter(({ type }) => type === 'page')
        .map(item => {
          return {
            ...item,
            video: data.find(({ id }) => id === item.video)
          }
        })

      const content = data.filter(
        ({ type }) =>
          type === 'article' || type === 'video' || type === 'gallery'
      )

      state.posts = [
        ...panoramas,
        ...toursOrLocations,
        ...categories,
        ...subjects,
        ...pages,
        ...content
      ]
    }
  },

  actions: {}
})

export default store
